/** CLE-550: load chat Clu's server-validated search criteria onto the page.
 *
 * The API checks people and company criteria against the same vocabularies the
 * page uses and returns them as camelCase filter keys (only the ones that
 * differ from the defaults). This turns them into the page's `q` payload, or
 * into filter objects for the landing box, without the legacy agent mapper,
 * which drops what it cannot map. Here an unknown key, a wrong value type or a
 * vocabulary miss comes back as an error and nothing is applied.
 *
 * The check is the MCP loader's own (`validateSearchFilters`) with the saved
 * exclusions the MCP URL refuses switched on. Looking one person up by name,
 * LinkedIn URL or Twitter handle stays refused.
 */
import { INITIAL_FILTERS, type SearchFilters } from '@/types/search-filters';
import { validateBooleanQuery } from '@/utils/boolean-query.util';
import { normalizeCalendarDate } from '@/utils/calendar-date.util';
import {
  INITIAL_COMPANY_FILTERS,
  buildCompanyFilters,
  type CompanySearchFilters,
} from '@/utils/company-search.utils';
import { encodeSearchQuery } from '@/utils/search-query-encoding';
import {
  carriesCompanyExcludes,
  validateSearchFilters,
} from './mcp-search-handoff';
import { validatePeopleExclusions } from './people-exclusion-validation';

/** The key the page reads the company-first slice from in a people `q`. */
const COMPOSITE_KEY = '__compositeCompanyFilters';
const MATCH_TYPES: readonly string[] = ['contains', 'exact', 'similar'];
const PEOPLE_DATE_FIELDS = [
  'profileUpdatedAfter',
  'joinedCurrentAfter',
  'pastLeftCompanyAfter',
] as const;

/** What a tool result's criteria come to.
 *  - absent: the result carries none (an older result): use the legacy mapper.
 *  - empty: it carries criteria but nothing differs from the defaults.
 *  - invalid: something does not fit the page's vocabulary; open nothing.
 *  - ready: `q` for the finder URL, plus the parsed filters for a caller that
 *    sets page state directly. */
export type CluPeopleHandoff =
  | { kind: 'absent' }
  | { kind: 'empty' }
  | { kind: 'invalid'; error: string }
  | {
      kind: 'ready';
      q: string;
      filters: SearchFilters;
      composite: CompanySearchFilters | null;
    };

export type CluCompanyHandoff =
  | { kind: 'absent' }
  | { kind: 'empty' }
  | { kind: 'invalid'; error: string }
  | { kind: 'ready'; q: string; filters: CompanySearchFilters };

/** A tool result for a search that did not run: refused, failed, or a second
 *  handoff in a turn. It may carry the filters it tried, for Clu's next turn
 *  to correct; nothing opens or runs from it. */
export function searchDidNotRun(result: Record<string, unknown>): boolean {
  return result.search_status === 'not_run' || Boolean(result.error);
}

function plain(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const hasKeys = (value: Record<string, unknown>) =>
  Object.keys(value).length > 0;

/** Same defaults, same key order: nothing in it narrows a search. */
const isDefault = (value: object, defaults: object) =>
  JSON.stringify(value) === JSON.stringify(defaults);

/** The loader's messages start with its MCP framing; this is not an MCP link. */
function reasonOf(error: unknown): string {
  const text = error instanceof Error ? error.message : 'invalid criteria.';
  return text.replace(/^Cannot restore MCP search: /, '');
}

function assertMatchType(value: string) {
  if (!MATCH_TYPES.includes(value))
    throw new Error('companyNameMatchType has an unsupported value.');
}

function checkedPeople(criteria: Record<string, unknown>): SearchFilters {
  const people = validateSearchFilters(criteria, INITIAL_FILTERS, 'people', {
    allowExclusions: true,
  });
  assertMatchType(people.companyNameMatchType);
  // The pure part of the page's compiler, which refuses these too: a bad date
  // the provider would read as no constraint, a malformed boolean query, a CRM
  // inclusion or an empty list scope. Refusing here keeps the user on the card
  // instead of sending them to a page that errors.
  for (const key of PEOPLE_DATE_FIELDS)
    if (people[key]) normalizeCalendarDate(people[key], key, key);
  for (const key of ['titleBooleanQuery', 'keywordsBooleanQuery'] as const) {
    const problem = validateBooleanQuery(people[key]);
    if (problem) throw new Error(`${key}: ${problem}`);
  }
  validatePeopleExclusions(people);
  return people;
}

/** Where company criteria are going. `search` is Find Companies, which takes
 * the excludes; `slice` is a people search's company-first slice, which never
 * does (the people criteria's own excludes do that job). */
type CompanyTarget = 'search' | 'slice';

function checkedCompany(
  criteria: Record<string, unknown>,
  where: CompanyTarget
): CompanySearchFilters {
  const company = validateSearchFilters(
    criteria,
    INITIAL_COMPANY_FILTERS,
    'company',
    { allowExclusions: true, companyExcludes: true }
  );
  assertMatchType(company.companyNameMatchType);
  // The page's own grammar for numbers, dates and domains.
  buildCompanyFilters(company);
  // An exclude the slice would not send is never loaded as if it were: the
  // search would run wider than the one Clu described.
  if (
    where === 'slice' &&
    carriesCompanyExcludes(company as unknown as Record<string, unknown>)
  )
    throw new Error(
      'company_criteria cannot carry exclusions; put them in the people criteria.'
    );
  return company;
}

/** `people_search`: its `criteria`, and the `company_criteria` that ride under
 * the namespaced key the page reads as the company-first slice. */
export function cluPeopleHandoff(
  criteria: unknown,
  companyCriteria?: unknown
): CluPeopleHandoff {
  if (criteria == null && companyCriteria == null) return { kind: 'absent' };
  const peopleInput = criteria ?? {};
  const companyInput = companyCriteria ?? {};
  if (!plain(peopleInput) || !plain(companyInput))
    return { kind: 'invalid', error: 'criteria must be an object.' };
  if (!hasKeys(peopleInput) && !hasKeys(companyInput)) return { kind: 'empty' };
  try {
    const filters = checkedPeople(peopleInput);
    const composite = hasKeys(companyInput)
      ? checkedCompany(companyInput, 'slice')
      : null;
    if (
      isDefault(filters, INITIAL_FILTERS) &&
      (!composite || isDefault(composite, INITIAL_COMPANY_FILTERS))
    )
      return { kind: 'empty' };
    const q = encodeSearchQuery({
      tab: 'people',
      filters: { ...filters, ...(composite && { [COMPOSITE_KEY]: composite }) },
    });
    return { kind: 'ready', q, filters, composite };
  } catch (error) {
    return { kind: 'invalid', error: reasonOf(error) };
  }
}

/** `company_search`: its `criteria`, excludes included. */
export function cluCompanyHandoff(criteria: unknown): CluCompanyHandoff {
  if (criteria == null) return { kind: 'absent' };
  if (!plain(criteria))
    return { kind: 'invalid', error: 'criteria must be an object.' };
  if (!hasKeys(criteria)) return { kind: 'empty' };
  try {
    const filters = checkedCompany(criteria, 'search');
    if (isDefault(filters, INITIAL_COMPANY_FILTERS)) return { kind: 'empty' };
    const q = encodeSearchQuery({ tab: 'company', filters: { ...filters } });
    return { kind: 'ready', q, filters };
  } catch (error) {
    return { kind: 'invalid', error: reasonOf(error) };
  }
}
